class CartDrawer extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('shopify:section:select', (event) => {
      if(event.detail.sectionId === `${this.dataset.section}`) {
        document.body.classList.add("overflow-hidden");
        document.querySelector('cart-drawer').classList.add('animate', 'active');
      }
    });
    
    document.addEventListener('shopify:section:deselect', (event) => {
      if(event.detail.sectionId === `${this.dataset.section}`) {
        document.body.classList.remove("overflow-hidden");
        document.querySelector('cart-drawer').classList.remove('animate', 'active');
      }
    });

    this.focusDirection = 1;
    this.onDrawerFocus = this.handleDrawerFocus.bind(this);
    this.onDrawerKeydown = this.handleDrawerKeydown.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.startFocusScope();
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    this.classList.remove('active');
    this.stopFocusScope();
    document.body.classList.remove('overflow-hidden');
    const trigger = this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble');
    if (trigger && !trigger.closest('[inert]')) trigger.focus({ preventScroll: true });
  }

  // Let the browser traverse payment shadow roots and frames itself. A cached
  // light-DOM "last button" would wrap at Checkout before reaching the wallets.
  startFocusScope() {
    removeTrapFocus();
    if (!this.focusScopeActive) {
      this.focusScopeActive = true;
      this.focusFrames = new Set();
      this.focusRoots = new Set();
      this.focusObserver = new MutationObserver(() => {
        this.ensureFocusGuards();
        this.getDrawerFocusTargets();
      });
      this.focusObserver.observe(this, { childList: true, subtree: true });
      document.addEventListener('focusin', this.onDrawerFocus, true);
      document.addEventListener('keydown', this.onDrawerKeydown, true);
    }
    this.ensureFocusGuards();
    this.getDrawerFocusTargets();
    this.querySelector('.drawer__inner')?.focus({ preventScroll: true });
  }

  stopFocusScope() {
    this.focusScopeActive = false;
    this.focusObserver?.disconnect();
    document.removeEventListener('focusin', this.onDrawerFocus, true);
    document.removeEventListener('keydown', this.onDrawerKeydown, true);
    this.focusFrames?.forEach(frame => {
      frame.removeEventListener('load', this.onFrameLoad);
      try { frame.contentDocument?.removeEventListener('keydown', this.onDrawerKeydown, true); } catch (_) {}
    });
    this.querySelectorAll('[data-cart-focus-guard]').forEach(guard => guard.remove());
    this.focusFrames?.clear();
    this.focusRoots?.clear();
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    this.stopFocusScope();
  }

  ensureFocusGuards() {
    const panel = this.querySelector('.drawer__inner');
    if (!panel || !this.focusScopeActive) return;
    ['start', 'end'].forEach(edge => {
      if (panel.querySelector(`[data-cart-focus-guard="${edge}"]`)) return;
      const guard = document.createElement('span');
      guard.dataset.cartFocusGuard = edge;
      guard.tabIndex = 0;
      guard.setAttribute('aria-hidden', 'true');
      guard.style.cssText = 'position:fixed;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);padding:0;margin:0;';
      if (edge === 'start') panel.prepend(guard);
      else panel.append(guard);
    });
  }

  isInsideDrawer(element) {
    while (element) {
      if (this.contains(element)) return true;
      const root = element.getRootNode();
      if (root.host) element = root.host;
      else {
        try { element = element.ownerDocument.defaultView.frameElement; } catch (_) { return false; }
      }
    }
    return false;
  }

  getDrawerFocusTargets() {
    const targets = [];
    const selector = 'a[href],area[href],button,input,select,textarea,iframe,object,summary,[tabindex],[contenteditable="true"],audio[controls],video[controls]';
    const visit = root => Array.from(root.children || []).forEach(element => {
      const style = element.ownerDocument.defaultView.getComputedStyle(element);
      if (element.hidden || element.inert || element.getAttribute('aria-hidden') === 'true' || element.getAttribute('aria-disabled') === 'true' || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return;
      if (element.matches(selector) && element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length) targets.push(element);
      if (element.shadowRoot) {
        if (this.focusScopeActive && !this.focusRoots.has(element.shadowRoot)) {
          this.focusRoots.add(element.shadowRoot);
          this.focusObserver.observe(element.shadowRoot, { childList: true, subtree: true });
        }
        visit(element.shadowRoot);
      } else if (element.localName === 'slot') {
        const assigned = element.assignedElements({ flatten: true });
        if (assigned.length) assigned.forEach(child => visit({ children: [child] }));
        else visit(element);
      } else visit(element);
      if (element.localName === 'iframe' && this.focusScopeActive) {
        if (!this.focusFrames.has(element)) {
          this.focusFrames.add(element);
          this.onFrameLoad ||= () => this.getDrawerFocusTargets();
          element.addEventListener('load', this.onFrameLoad);
        }
        try {
          const frameDocument = element.contentDocument;
          if (frameDocument) {
            frameDocument.addEventListener('keydown', this.onDrawerKeydown, true);
            visit(frameDocument);
          }
        } catch (_) { /* Cross-origin payment frames keep their native Tab order. */ }
      }
    });
    visit(this.querySelector('.drawer__inner') || this);
    return targets;
  }

  focusDrawerEdge(direction) {
    const targets = this.getDrawerFocusTargets();
    const target = direction < 0 ? targets[targets.length - 1] : targets[0];
    (target || this.querySelector('.drawer__inner'))?.focus({ preventScroll: true });
  }

  handleDrawerFocus(event) {
    if (!this.focusScopeActive || this.nativeReverseWrap) return;
    const element = event.composedPath()[0];
    if (element.dataset?.cartFocusGuard) {
      this.focusDrawerEdge(element.dataset.cartFocusGuard === 'start' ? -1 : 1);
    } else if (!this.isInsideDrawer(element)) this.focusDrawerEdge(this.focusDirection);
    else this.getDrawerFocusTargets();
  }

  handleDrawerKeydown(event) {
    if (!this.focusScopeActive) return;
    if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    } else if (event.key === 'Tab' || event.code === 'Tab') {
      this.focusDirection = event.shiftKey ? -1 : 1;
      const element = event.composedPath()[0];
      if (event.shiftKey && (element === this.querySelector('.drawer__inner') || element === this.getDrawerFocusTargets()[0])) {
        // Start the browser's own backward navigation immediately after the
        // wallets. It can enter even a cross-origin frame's final control,
        // which script cannot inspect or focus directly.
        this.nativeReverseWrap = true;
        this.querySelector('[data-cart-focus-guard="end"]')?.focus({ preventScroll: true });
        this.nativeReverseWrap = false;
        return;
      }
      if (!this.isInsideDrawer(element) || element === this.querySelector('.drawer__inner')) {
        event.preventDefault();
        this.focusDrawerEdge(this.focusDirection);
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Keep native wallets immediately available on desktop and compact on mobile.
// Each cart section replacement reconnects this element; no payment DOM is cloned.
class CanvoilCartExpress extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.onLayoutChange = () => {
      const active = document.activeElement;
      if (this.mobileQuery.matches) {
        if (active && active !== this.summary && this.details.contains(active)) {
          this.summary.focus({ preventScroll: true });
        }
        this.details.open = false;
      } else {
        this.details.open = true;
        if (active === this.summary) {
          const drawer = this.closest('cart-drawer');
          const checkout = drawer?.querySelector('#CartDrawer-Checkout');
          const target = checkout && !checkout.disabled && !checkout.closest('[inert]')
            ? checkout
            : drawer?.querySelector('.drawer__inner');
          target?.focus({ preventScroll: true });
        }
      }
    };
    this.onLayoutChange();
    this.mobileQuery.addEventListener('change', this.onLayoutChange);
  }

  disconnectedCallback() {
    this.mobileQuery?.removeEventListener('change', this.onLayoutChange);
  }
}

customElements.define('canvoil-cart-express', CanvoilCartExpress);
